import type { Animation, AnimationCredit, Rating, UsageTerms, Visibility } from '../types';
import { mockAnimations } from '../data/mockData';
import { getIdToken } from '../auth/auth';
import { recommendShorts } from '../lib/shortsRecommend';
import type { AdminTag, AdminCategory } from './developers';

/** タグマスタの公開情報。developers.ts の AdminTag と同一形状（name, description?, createdAt）。 */
export type TagInfo = AdminTag;

/** カテゴリマスタの公開情報。developers.ts の AdminCategory と同一形状（name, description?, createdAt）。 */
export type CategoryInfo = AdminCategory;

/**
 * API クライアント。
 * VITE_API_BASE_URL が設定されていれば SAM バックエンド（HTTP API）を、
 * 未設定（単体デモなど）ならモックデータ + localStorage を使う。
 * 公式サンプルも通常のアニメーションレコードとして DB に保存されているため、
 * 一覧・詳細取得のクライアント側合成は不要（他のアニメーションと同じ経路で取得する）。
 *
 * バックエンドのエンドポイント:
 *   GET    /animations                一覧（public のみ、?limit・?cursor でページング）
 *   GET    /animations/{id}           詳細（private は所有者のみ）
 *   POST   /uploads                   presigned PUT URL 発行（要ログイン）
 *   POST   /animations                メタデータ登録（要ログイン）
 *   PATCH  /animations/{id}           メタデータの部分更新・アプリ配信設定の変更（所有者のみ）
 *   POST   /animations/{id}/favorite  お気に入り追加（要ログイン）
 *   DELETE /animations/{id}/favorite  お気に入り解除（要ログイン）
 *   GET    /me/animations             自分の投稿一覧（要ログイン、?limit・?cursor でページング）
 *   GET    /me/favorites              お気に入り一覧（要ログイン）
 *   GET    /me/connected-apps         連携中の OAuth アプリ一覧（要ログイン）
 *   DELETE /me/connected-apps/{id}    連携解除（要ログイン）
 *   GET    /me/profile                自分のプロフィール取得（要ログイン。未作成なら null）
 *   PUT    /me/profile                プロフィール作成/更新（要ログイン）
 *   POST   /me/profile/avatar-upload-url  アイコン用 presigned PUT URL 発行（要ログイン）
 *   GET    /users/{sub}               他人の公開プロフィール取得（無ければ404）
 *   GET    /users/{sub}/animations    他人の公開VRMA一覧（新着順、?limit・?cursor でページング）
 *   POST   /users/{sub}/follow        フォロー（要ログイン・冪等）
 *   DELETE /users/{sub}/follow        フォロー解除（要ログイン・冪等）
 *   GET    /users/{sub}/follow-status 自分がフォロー中か（要ログイン）
 *   GET    /me/following              フォロー中ユーザー一覧（要ログイン、フォロー日時降順）
 *   GET    /me/following-animations   フォロー中ユーザーの新着VRMA（要ログイン、?limit・?cursor でページング）
 *   GET    /tags                      タグマスタ一覧（認証不要）
 *   GET    /categories                カテゴリマスタ一覧（認証不要）
 *   GET    /shorts/feed               Shorts フィード（レコメンド。ログイン時のみパーソナライズ）
 *   POST   /contact                   お問い合わせフォームの送信（認証不要）
 *   POST   /animations/{id}/report     アニメーションの通報（要ログイン）
 *   POST   /animations/{id}/count      再生数/DL数のカウント（認証不要）
 *
 * 一覧系（/animations, /me/animations, /users/{sub}/animations, /me/following-animations）は
 * カーソルページング対応。本文は従来通り JSON 配列のまま返り、次ページの位置は
 * X-Next-Cursor レスポンスヘッダで渡される（無ければ最終ページ）。クライアント側では
 * PagedAnimations（{ items, nextCursor }）にまとめて返す。
 */
const API_BASE = import.meta.env.VITE_API_BASE_URL;

/** ログイン中なら Authorization ヘッダを付ける。 */
async function authHeaders(): Promise<Record<string, string>> {
  const token = await getIdToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function apiFetch(path: string, init?: RequestInit): Promise<Response> {
  const headers = { ...(await authHeaders()), ...(init?.headers as Record<string, string>) };
  return fetch(`${API_BASE}${path}`, { ...init, headers });
}

// ---- モックモード用のローカルストア ---------------------------------------

const LOCAL_KEY = 'vrma-hub:local-animations';
const FAV_KEY = 'vrma-hub:local-favorites';
// developers.ts の TAGS_KEY と同じキー（管理コンソールで登録したタグを共有する）。
const TAGS_STORAGE_KEY = 'vrma-hub:admin-tags';
// developers.ts の CATEGORIES_KEY と同じキー（管理コンソールで登録したカテゴリを共有する）。
const CATEGORIES_STORAGE_KEY = 'vrma-hub:admin-categories';

// blob: URL を含む完全な状態はモジュールスコープのメモリ（このキャッシュ）を正とする。
// localStorage は再読込後も一覧・お気に入りを残すための永続化専用で、blob: URL は
// リロードで無効になるため保存時に取り除く。もし loadLocal() が毎回 localStorage を
// 読み直すと、投稿直後の詳細ページ表示でも「保存済み＝blob URL 抜き」の内容しか
// 見えず vrma が再生できなくなるため、同一セッション中は blob URL 込みでこのメモリ上の
// リストを参照できるようにしている。
let localAnimationsCache: Animation[] | null = null;

function loadLocal(): Animation[] {
  if (localAnimationsCache !== null) return localAnimationsCache;
  try {
    const raw = localStorage.getItem(LOCAL_KEY);
    localAnimationsCache = raw ? (JSON.parse(raw) as Animation[]) : [];
  } catch {
    localAnimationsCache = [];
  }
  return localAnimationsCache;
}

function saveLocal(items: Animation[]): void {
  // メモリ上のキャッシュは blob URL を含む完全版のまま保持する（同一セッション中の再生用）。
  localAnimationsCache = items;
  // localStorage には blob: URL は再読込で無効になるため除去してから永続化する。
  const persistable = items.map(({ vrmaUrl, ...rest }) =>
    vrmaUrl?.startsWith('blob:') ? rest : { ...rest, vrmaUrl },
  );
  localStorage.setItem(LOCAL_KEY, JSON.stringify(persistable));
}

function loadFavIds(): string[] {
  try {
    const raw = localStorage.getItem(FAV_KEY);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
}

function saveFavIds(ids: string[]): void {
  localStorage.setItem(FAV_KEY, JSON.stringify(ids));
}

// ---- 一覧・詳細 -------------------------------------------------------------

/** カーソルページングの取得オプション。どちらも省略可能（省略時は先頭から既定件数）。 */
export interface ListPageOptions {
  /** 取得件数。省略時はエンドポイントごとの既定値。 */
  limit?: number;
  /** 前回取得時の nextCursor。省略時は先頭から取得する。 */
  cursor?: string;
  tags?: string[];
  category?: string;
  q?: string;
}

export interface AnimationTagCount {
  name: string;
  count: number;
}

export type AnimationTagScope = 'public' | 'mine' | 'following';

function addListFilters(params: URLSearchParams, opts: ListPageOptions): void {
  if (opts.tags?.length) params.set('tags', opts.tags.join(','));
  if (opts.category) params.set('category', opts.category);
  if (opts.q) params.set('q', opts.q);
}

/** カーソルページングの取得結果。nextCursor が無ければ最終ページ。 */
export interface PagedAnimations {
  items: Animation[];
  nextCursor?: string;
}

/**
 * モックモード用のオフセットベースのページング。バックエンドの X-Next-Cursor と同じ
 * 「不透明な文字列」として扱えるよう、cursor には単純な次オフセットの文字列を使う
 * （実データが少ないモックでは DynamoDB 相当のカーソル構造を再現する意味が薄いため）。
 */
function paginateMock<T>(all: T[], opts: ListPageOptions, defaultLimit: number): { items: T[]; nextCursor?: string } {
  const limit = opts.limit && opts.limit > 0 ? opts.limit : defaultLimit;
  const offsetParsed = opts.cursor ? Number(opts.cursor) : 0;
  const offset = Number.isFinite(offsetParsed) && offsetParsed > 0 ? offsetParsed : 0;
  const items = all.slice(offset, offset + limit);
  const nextCursor = offset + limit < all.length ? String(offset + limit) : undefined;
  return { items, nextCursor };
}

const DEFAULT_LIST_LIMIT = 100;

/** 公開アニメーションの一覧。 */
export async function fetchAnimations(opts: ListPageOptions = {}): Promise<PagedAnimations> {
  if (API_BASE) {
    const params = new URLSearchParams();
    if (opts.limit) params.set('limit', String(opts.limit));
    if (opts.cursor) params.set('cursor', opts.cursor);
    addListFilters(params, opts);
    const qs = params.toString();
    const res = await fetch(`${API_BASE}/animations${qs ? `?${qs}` : ''}`);
    if (!res.ok) throw new Error(`一覧の取得に失敗しました (${res.status})`);
    const items = (await res.json()) as Animation[];
    return { items, nextCursor: res.headers.get('X-Next-Cursor') ?? undefined };
  }
  // モック: ローカル投稿のうち public のみ + サンプル。
  const local = loadLocal().filter((a) => (a.visibility ?? 'public') === 'public');
  const filtered = [...local, ...mockAnimations].filter((animation) =>
    (opts.tags ?? []).every((tag) => animation.tags.includes(tag)) &&
    (!opts.category || (opts.category === '__uncategorized__' ? !animation.category : animation.category === opts.category)) &&
    (!opts.q || animation.title.toLowerCase().includes(opts.q.toLowerCase()) || animation.author.toLowerCase().includes(opts.q.toLowerCase())),
  );
  return paginateMock(filtered, opts, DEFAULT_LIST_LIMIT);
}

/** 一覧条件に一致するアニメーション内のタグ使用数。 */
export async function fetchAnimationTagCounts(
  scope: AnimationTagScope,
  opts: Omit<ListPageOptions, 'cursor' | 'limit'> = {},
): Promise<AnimationTagCount[]> {
  if (!API_BASE) {
    const page = scope === 'mine' ? await fetchMyAnimations(opts) : scope === 'following' ? await fetchFollowingAnimations(opts) : await fetchAnimations(opts);
    const counts = new Map<string, number>();
    for (const animation of page.items) for (const tag of new Set(animation.tags)) counts.set(tag, (counts.get(tag) ?? 0) + 1);
    return [...counts].map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count || a.name.localeCompare(b.name)).slice(0, 30);
  }
  const params = new URLSearchParams({ scope });
  addListFilters(params, opts);
  const res = scope === 'public'
    ? await fetch(`${API_BASE}/animations/tags?${params}`)
    : await apiFetch(`/animations/tags?${params}`);
  if (!res.ok) throw new Error(`タグ集計の取得に失敗しました (${res.status})`);
  return (await res.json()) as AnimationTagCount[];
}

/** タグマスタの一覧（公開情報）。管理者が追加したタグだけが返る。 */
export async function fetchTags(): Promise<TagInfo[]> {
  if (API_BASE) {
    const res = await fetch(`${API_BASE}/tags`);
    if (!res.ok) throw new Error(`タグ一覧の取得に失敗しました (${res.status})`);
    return (await res.json()) as TagInfo[];
  }
  try {
    const raw = localStorage.getItem(TAGS_STORAGE_KEY);
    const tags = raw ? (JSON.parse(raw) as TagInfo[]) : [];
    return [...tags].sort((a, b) => new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime());
  } catch {
    return [];
  }
}

/** カテゴリマスタの一覧（公開情報）。管理者が追加したカテゴリだけが返る。 */
export async function fetchCategories(): Promise<CategoryInfo[]> {
  if (API_BASE) {
    const res = await fetch(`${API_BASE}/categories`);
    if (!res.ok) throw new Error(`カテゴリ一覧の取得に失敗しました (${res.status})`);
    return (await res.json()) as CategoryInfo[];
  }
  try {
    const raw = localStorage.getItem(CATEGORIES_STORAGE_KEY);
    const categories = raw ? (JSON.parse(raw) as CategoryInfo[]) : [];
    return [...categories].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  } catch {
    return [];
  }
}

/** 1 件取得。private は所有者としてログインしていれば取得できる。 */
export async function fetchAnimation(id: string): Promise<Animation | undefined> {
  if (API_BASE) {
    const res = await apiFetch(`/animations/${id}`);
    if (res.status === 404) return undefined;
    if (!res.ok) throw new Error(`詳細の取得に失敗しました (${res.status})`);
    return (await res.json()) as Animation;
  }
  return [...loadLocal(), ...mockAnimations].find((a) => a.id === id);
}

// ---- Shorts フィード ---------------------------------------------------------

export interface ShortsFeedOptions {
  /** 取得件数。未指定は10件。 */
  limit?: number;
  /** 除外する（表示済みの）ID。無限スクロールの追加取得時に渡す。 */
  seen?: string[];
}

/**
 * Shorts（縦スワイプフィード）向けのレコメンド取得。
 * API モードはログイン中のみ Authorization ヘッダが付き、バックエンド側でパーソナライズされる
 * （バックエンドは並行実装中。レスポンスは一覧取得と同じ Animation[] 形状）。
 * モックモードでは母集団（ローカル投稿の public のみ + サンプル）を lib/shortsRecommend.ts の
 * recommendShorts() でスコアリング・サンプリングし、クライアント側だけでレコメンドを再現する。
 */
export async function fetchShortsFeed(opts: ShortsFeedOptions = {}): Promise<Animation[]> {
  const { limit = 10, seen = [] } = opts;
  if (API_BASE) {
    const params = new URLSearchParams({ limit: String(limit) });
    if (seen.length > 0) params.set('seen', seen.join(','));
    const res = await apiFetch(`/shorts/feed?${params.toString()}`);
    if (!res.ok) throw new Error(`Shorts フィードの取得に失敗しました (${res.status})`);
    return (await res.json()) as Animation[];
  }
  const pool = [...loadLocal().filter((a) => (a.visibility ?? 'public') === 'public'), ...mockAnimations];
  const favIds = new Set(loadFavIds());
  const favorites = pool.filter((a) => favIds.has(a.id));
  return recommendShorts(pool, { seen, favorites, limit });
}

// ---- 画像からのポーズ生成（サーバー推論）---------------------------------------

/** pose-server が返す1ボーン分のクォータニオン（VRM1 正規化ボーン向けローカル回転）。 */
export interface PoseInferenceQuat {
  x: number;
  y: number;
  z: number;
  w: number;
}

/**
 * SOMA（77関節）ヒューマノイドの axis-angle ポーズ + root 位置。
 * POST /pose/animation の keyframes にそのまま渡せる形。旧バックエンド（未対応）は
 * meta 自体を返さないため、PoseInferenceResult.meta.somaPose は省略可能として扱う。
 */
export interface SomaPose {
  /** 77 関節 × axis-angle (x, y, z) の 77x3 配列。 */
  localJointsRot: number[][];
  /** root（腰）位置 [x, y, z]。 */
  rootPosition: number[];
}

export interface PoseInferenceResult {
  bones: Record<string, PoseInferenceQuat>;
  rootTranslation?: { x: number; y: number; z: number };
  /** VRMボーン名→0〜1の信頼度。複数視点マージ（mergeMultiViewPoses）で採用視点の判定に使う。 */
  boneConfidences?: Record<string, number>;
  /** 複数画像からのアニメーション生成（startPoseAnimation）用の中間データ。旧バックエンドでは付かない。 */
  meta?: { somaPose?: SomaPose };
}

/**
 * 画像からのポーズ推定（generatePoseFromImage）が 503 を返したときに、backend が
 * 正規化して返す state を保持する専用エラー。呼び出し側（serverPose.ts の
 * estimatePoseWithAutoStart）が state === 'starting' のときだけ起動待ちに入るかどうかを
 * instanceof で判定するために使う。message は従来どおり backend の error 文言をそのまま使う。
 */
export class PoseServerUnavailableError extends Error {
  constructor(
    message: string,
    public readonly state: 'starting' | 'not-accepting' | 'offline',
  ) {
    super(message);
    this.name = 'PoseServerUnavailableError';
  }
}

/**
 * 画像1枚から VRM ヒューマノイドポーズを推定する（要ログイン）。
 * バックエンドが pose-server（PC常駐 GEM-X 推論サーバー）へ中継する。
 * モックモード（バックエンド未接続）では利用できない。
 */
export async function generatePoseFromImage(imageBase64: string, mimeType: string): Promise<PoseInferenceResult> {
  if (!API_BASE) {
    throw new Error('この機能はバックエンド接続時のみ利用できます');
  }
  const res = await apiFetch('/pose/from-image', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ image: imageBase64, mimeType }),
  });
  if (res.status === 401) throw new Error('ログインが必要です');
  if (res.status === 422) {
    const body = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new Error(body?.error ?? 'ポーズを推定できませんでした。');
  }
  if (res.status === 503) {
    // オフライン（サーバー未起動・トンネル断）とパイプラインエラー（500中継）とで
    // バックエンドが返す文言が異なるため、固定文言にせずそのまま表示する。
    // state は estimatePoseWithAutoStart() が起動待ちに入るかどうかの判定に使う。
    const body = (await res.json().catch(() => null)) as { error?: string; state?: string } | null;
    const message = body?.error ?? '推論サーバーが現在オフラインです。時間をおいてお試しください';
    const state = body?.state === 'starting' || body?.state === 'not-accepting' ? body.state : 'offline';
    throw new PoseServerUnavailableError(message, state);
  }
  if (res.status === 504) throw new Error('推定がタイムアウトしました');
  if (!res.ok) throw new Error(`ポーズの推定に失敗しました (${res.status})`);
  return (await res.json()) as PoseInferenceResult;
}

/**
 * pose-server が現在オンラインかどうかを確認する（要ログイン）。
 * サーバー側の状態にかかわらず 200 で返る前提のため、失敗時は「確認できなかった」
 * ものとして呼び出し側が online: false 相当に扱えばよい。
 * state は 'running' | 'starting' | 'stopping' | 'stopped' | 'unhealthy' | 'unreachable' | 'unconfigured'。
 * accepting は管理者が設定した「利用者からの画像受付」フラグ（true なら画像を送ると自動起動する）。
 * 旧バックエンド（未対応）はレスポンスに accepting を含めないため、その場合は true にフォールバックする。
 * 到達不能・未設定時も「受付停止中」と誤解させないよう true にフォールバックする。
 * モックモード（バックエンド未接続）では常に unconfigured 扱いにする。
 */
export async function fetchPoseServerHealth(): Promise<{ online: boolean; state: string; accepting: boolean }> {
  if (!API_BASE) return { online: false, state: 'unconfigured', accepting: true };
  try {
    const res = await apiFetch('/pose/health');
    if (!res.ok) return { online: false, state: 'unreachable', accepting: true };
    const body = (await res.json()) as { online?: boolean; state?: string; accepting?: boolean };
    return {
      online: body.online === true,
      state: body.state ?? 'unreachable',
      accepting: body.accepting ?? true,
    };
  } catch {
    return { online: false, state: 'unreachable', accepting: true };
  }
}

// ---- 複数画像からのアニメーション生成（サーバー推論・非同期ジョブ）------------------------------

export interface PoseAnimationKeyframeInput {
  localJointsRot: number[][];
  rootPosition: number[];
}

export interface StartPoseAnimationParams {
  /** 2〜6件。順序どおりに intervalSeconds 間隔のキーポーズとして扱われる。 */
  keyframes: PoseAnimationKeyframeInput[];
  /** キーフレーム間の秒数（1〜10。バックエンドの1セグメントあたりの生成上限に合わせた範囲）。 */
  intervalSeconds: number;
  /** 生成プロンプト（英語推奨、最大300文字。バックエンドでも同じ上限を検証する）。 */
  prompt: string;
  /** 出力フレームレート（5〜30）。省略時はバックエンド既定値。 */
  fps?: number;
  seed?: number | null;
}

/**
 * 複数画像から推定したキーポーズ列（SomaPose）を渡して、Kimodo によるアニメーション生成ジョブを
 * 開始する（要ログイン）。GPU が1枚しかないためサーバー側は同時に1ジョブしか実行できず、既に
 * 実行中のジョブがある場合は 409 が返る。「今は混んでいる」だけで致命的なエラーではないため、
 * 呼び出し側は後で再実行できる旨が伝わるメッセージとして扱うこと。
 */
export async function startPoseAnimation(params: StartPoseAnimationParams): Promise<{ jobId: string }> {
  if (!API_BASE) {
    throw new Error('この機能はバックエンド接続時のみ利用できます');
  }
  const res = await apiFetch('/pose/animation', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
  if (res.status === 401) throw new Error('ログインが必要です');
  if (res.status === 400) {
    const body = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new Error(body?.error ?? '入力内容を確認してください。');
  }
  if (res.status === 409) {
    const body = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new Error(body?.error ?? '別のジョブが実行中です。しばらくしてから再度お試しください。');
  }
  if (res.status === 503) {
    const body = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new Error(body?.error ?? '推論サーバーが現在オフラインです。時間をおいてお試しください');
  }
  if (!res.ok) throw new Error(`アニメーション生成の開始に失敗しました (${res.status})`);
  return (await res.json()) as { jobId: string };
}

export type PoseAnimationJobStatus =
  | 'queued'
  | 'stopping-pose-server'
  | 'starting-kimodo'
  | 'generating'
  | 'done'
  | 'failed';

/** 生成された1フレーム分。bones は generatePoseFromImage と同じ VRM1 正規化ボーン名のキー。 */
export interface PoseAnimationFrame {
  timeSec: number;
  bones: Record<string, PoseInferenceQuat>;
  rootTranslation: { x: number; y: number; z: number };
}

export interface PoseAnimationResult {
  version: number;
  fps: number;
  durationSeconds: number;
  frames: PoseAnimationFrame[];
  meta: Record<string, unknown>;
}

export interface PoseAnimationStatus {
  jobId: string;
  status: PoseAnimationJobStatus;
  progress?: { step: string; current: number; total: number };
  /** サーバーが返す日本語の進捗メッセージ。そのまま表示してよい。 */
  message?: string;
  result?: PoseAnimationResult;
  error?: string | null;
}

/**
 * ジョブ状態の取得時、推論サーバーに一時的に到達できない（503・fetch自体の失敗）ことを示すエラー。
 * 自宅PCから Cloudflare Tunnel 経由で公開しているため瞬断が起こり得るが、ジョブ自体はサーバー側で
 * 走り続けている。呼び出し側（ポーリングループ）はこれを即座に失敗扱いにせず、数回（目安3〜5回）
 * 連続で発生した場合にのみエラー表示に落とすこと。404（未知の jobId）や、200応答内での
 * status: 'failed' は確定的な失敗のためこのクラスにはしない（通常の Error / 通常の返り値のまま扱う）。
 */
export class PoseAnimationUnreachableError extends Error {}

/**
 * アニメーション生成ジョブの状態を取得する（要ログイン）。数秒間隔でポーリングする想定。
 * - 404（未知の jobId）は確定的な失敗として通常の Error を投げる。
 * - 503・fetch自体の失敗（一時的にサーバーへ到達できない）は PoseAnimationUnreachableError を
 *   投げるので、呼び出し側で区別してリトライすること。
 * - ジョブ自体の失敗（バリデーションエラー・生成エラー・応答サイズ超過など）は throw せず、
 *   HTTP 200 のまま body.status === 'failed'（body.error にメッセージ）として返る。
 */
export async function getPoseAnimationStatus(jobId: string): Promise<PoseAnimationStatus> {
  if (!API_BASE) {
    throw new Error('この機能はバックエンド接続時のみ利用できます');
  }
  let res: Response;
  try {
    res = await apiFetch(`/pose/animation/${encodeURIComponent(jobId)}`);
  } catch (e) {
    throw new PoseAnimationUnreachableError(e instanceof Error ? e.message : String(e));
  }
  if (res.status === 401) throw new Error('ログインが必要です');
  if (res.status === 404) throw new Error('指定されたジョブが見つかりません。');
  if (res.status === 503) {
    const body = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new PoseAnimationUnreachableError(body?.error ?? '推論サーバーに一時的に到達できません。');
  }
  if (!res.ok) throw new Error(`ジョブ状態の取得に失敗しました (${res.status})`);
  return (await res.json()) as PoseAnimationStatus;
}

// ---- アップロード -----------------------------------------------------------

export interface UploadFields {
  title: string;
  description: string;
  visibility: Visibility;
  tags: string[];
  /** カテゴリ名（最大1つ、任意）。未指定は「未分類」扱い。 */
  category?: string;
  file: File;
  /** サムネイル画像（ユーザー選択 or 自動生成）。省略時はサムネイル無しで登録する。 */
  thumbnail?: Blob | null;
  /** アセットのクレジット情報。指定時のみ登録する。 */
  credit?: AnimationCredit;
  /** 投稿者以外の利用条件。未指定なら credit なしの投稿と同様、指定しない。 */
  usageTerms?: UsageTerms;
  /** 閲覧制限。未指定は 'all' 扱い。 */
  rating?: Rating;
  /**
   * 公開確認モーダルで権利者確認・利用規約同意の両方にチェックした場合のみ true。
   * visibility が public のときサーバー側で必須（PublishConfirmModal 参照）。
   */
  rightsAttested?: boolean;
}

interface PresignResponse {
  uploadUrl: string;
  key: string;
  publicUrl: string;
}

/** dataURL（data:image/...;base64,...）に変換する。モックモードでの永続化用。 */
function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error ?? new Error('画像の読み込みに失敗しました'));
    reader.readAsDataURL(blob);
  });
}

/**
 * .vrma を presigned PUT で S3 へアップロードし、公開URLを返す。
 * uploadAnimation / updateAnimation の両方から使う。
 */
async function putVrmaFile(file: File): Promise<string> {
  // 大きな .vrma は API Gateway を通さず S3 へ直接アップロードする。
  // 署名時の Content-Type と PUT の Content-Type は一致させる必要がある。
  const contentType = file.type || 'application/octet-stream';

  const upRes = await apiFetch('/uploads', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ filename: file.name, contentType }),
  });
  if (upRes.status === 401) throw new Error('ログインが必要です');
  if (!upRes.ok) throw new Error(`アップロードURLの取得に失敗しました (${upRes.status})`);
  const { uploadUrl, publicUrl } = (await upRes.json()) as PresignResponse;

  const putRes = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': contentType },
    body: file,
  });
  if (!putRes.ok) throw new Error(`S3 へのアップロードに失敗しました (${putRes.status})`);
  return publicUrl;
}

/** サムネイル画像を presigned PUT で S3 へアップロードし、公開URLを返す。 */
async function putThumbnail(thumbnail: Blob): Promise<string> {
  const thumbType = thumbnail.type || 'image/jpeg';
  const thumbUpRes = await apiFetch('/uploads', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ kind: 'thumbnail', filename: 'thumbnail.jpg', contentType: thumbType }),
  });
  if (!thumbUpRes.ok) throw new Error(`サムネイルURLの取得に失敗しました (${thumbUpRes.status})`);
  const { uploadUrl: thumbUploadUrl, publicUrl: thumbPublicUrl } = (await thumbUpRes.json()) as PresignResponse;
  const thumbPutRes = await fetch(thumbUploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': thumbType },
    body: thumbnail,
  });
  if (!thumbPutRes.ok) throw new Error(`サムネイルのアップロードに失敗しました (${thumbPutRes.status})`);
  return thumbPublicUrl;
}

/**
 * アップロード（要ログイン）。
 * API があれば presigned 3 段フロー（発行 → S3 へ直接 PUT → メタ登録）、
 * 無ければ blob URL でローカル保存。
 */
export async function uploadAnimation(fields: UploadFields): Promise<Animation> {
  if (API_BASE) {
    // 1) .vrma を S3 へ直接 PUT
    const publicUrl = await putVrmaFile(fields.file);

    // 2) サムネイルがあれば同様に presigned PUT で先にアップロード
    const thumbnailUrl = fields.thumbnail ? await putThumbnail(fields.thumbnail) : undefined;

    // 3) メタデータ登録。
    const metaRes = await apiFetch('/animations', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        title: fields.title,
        description: fields.description,
        visibility: fields.visibility,
        tags: fields.tags,
        ...(fields.category ? { category: fields.category } : {}),
        vrmaUrl: publicUrl,
        ...(thumbnailUrl ? { thumbnailUrl } : {}),
        ...(fields.credit ? { credit: fields.credit } : {}),
        ...(fields.usageTerms ? { usageTerms: fields.usageTerms } : {}),
        ...(fields.rating ? { rating: fields.rating } : {}),
        ...(fields.rightsAttested ? { rightsAttested: fields.rightsAttested } : {}),
      }),
    });
    if (!metaRes.ok) throw new Error(`登録に失敗しました (${metaRes.status})`);
    return (await metaRes.json()) as Animation;
  }

  // ローカルモード: blob URL を作ってインメモリ + localStorage に保存。
  // サムネイルは dataURL に変換して保存する（data: URL は永続化して良い）。
  // credit はそのまま保存する。
  const thumbnailUrl = fields.thumbnail ? await blobToDataUrl(fields.thumbnail) : undefined;
  const animation: Animation = {
    id: `local-${Date.now()}`,
    title: fields.title,
    description: fields.description,
    author: 'you',
    ownerId: 'local',
    visibility: fields.visibility,
    tags: fields.tags,
    ...(fields.category ? { category: fields.category } : {}),
    thumbnailUrl,
    vrmaUrl: URL.createObjectURL(fields.file),
    likes: 0,
    createdAt: new Date().toISOString(),
    ...(fields.credit ? { credit: fields.credit } : {}),
    ...(fields.usageTerms ? { usageTerms: fields.usageTerms } : {}),
    ...(fields.rating ? { rating: fields.rating } : {}),
  };
  saveLocal([animation, ...loadLocal()]);
  return animation;
}

// ---- 編集 ---------------------------------------------------------------------

export interface UpdateAnimationFields {
  title: string;
  description: string;
  visibility: Visibility;
  tags: string[];
  /** カテゴリ名（最大1つ）。空文字列を渡すと「未分類」に戻す。常に送る（tags/visibility と同様）。 */
  category: string;
  /** 差し替える .vrma。undefined なら既存のファイルを据え置く。 */
  file?: File | null;
  /** 差し替えるサムネイル。undefined なら既存のサムネイルを据え置く。 */
  thumbnail?: Blob | null;
  /** クレジット。null を渡すと削除する。undefined なら据え置く。 */
  credit?: AnimationCredit | null;
  usageTerms?: UsageTerms;
  rating?: Rating;
  /**
   * 公開確認モーダルで権利者確認・利用規約同意の両方にチェックした場合のみ true。
   * private→public への変更時、または既存に rightsAttestedAt が無いまま public にする場合、
   * サーバー側で必須（PublishConfirmModal 参照）。
   */
  rightsAttested?: boolean;
}

/**
 * アニメーション情報の編集（所有者のみ）。アップロード画面（編集モード）から呼ばれる。
 * file を指定した場合のみ .vrma を再アップロードし、vrmaUrl を差し替える。
 */
export async function updateAnimation(id: string, fields: UpdateAnimationFields): Promise<Animation> {
  if (API_BASE) {
    const vrmaUrl = fields.file ? await putVrmaFile(fields.file) : undefined;
    const thumbnailUrl = fields.thumbnail ? await putThumbnail(fields.thumbnail) : undefined;

    const res = await apiFetch(`/animations/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        title: fields.title,
        description: fields.description,
        visibility: fields.visibility,
        tags: fields.tags,
        category: fields.category,
        ...(vrmaUrl ? { vrmaUrl } : {}),
        ...(thumbnailUrl ? { thumbnailUrl } : {}),
        ...(fields.credit !== undefined ? { credit: fields.credit } : {}),
        ...(fields.usageTerms ? { usageTerms: fields.usageTerms } : {}),
        ...(fields.rating ? { rating: fields.rating } : {}),
        ...(fields.rightsAttested ? { rightsAttested: fields.rightsAttested } : {}),
      }),
    });
    if (res.status === 401) throw new Error('ログインが必要です');
    if (!res.ok) throw new Error(`更新に失敗しました (${res.status})`);
    return (await res.json()) as Animation;
  }

  // ローカルモード: uploadAnimation のローカル実装と同じ方針。
  const items = loadLocal();
  const target = items.find((a) => a.id === id);
  if (!target) throw new Error('ローカル投稿のみ編集できます');
  const thumbnailUrl = fields.thumbnail ? await blobToDataUrl(fields.thumbnail) : undefined;
  const updatedItem: Animation = {
    ...target,
    title: fields.title,
    description: fields.description,
    visibility: fields.visibility,
    tags: fields.tags,
    ...(fields.file ? { vrmaUrl: URL.createObjectURL(fields.file) } : {}),
    ...(thumbnailUrl ? { thumbnailUrl } : {}),
    ...(fields.usageTerms ? { usageTerms: fields.usageTerms } : {}),
    ...(fields.rating ? { rating: fields.rating } : {}),
  };
  if (fields.credit === null) {
    delete updatedItem.credit;
  } else if (fields.credit !== undefined) {
    updatedItem.credit = fields.credit;
  }
  if (fields.category) {
    updatedItem.category = fields.category;
  } else {
    delete updatedItem.category;
  }
  const nextItems = items.map((a) => (a.id === id ? updatedItem : a));
  saveLocal(nextItems);
  return updatedItem;
}

/**
 * アプリ配信設定を変更する（所有者のみ）。
 * appClientIds に含めたアプリの OAuth access_token 経由なら、visibility に関係なく全ユーザーが閲覧できる。
 */
export async function updateAnimationApps(id: string, appClientIds: string[]): Promise<Animation> {
  if (API_BASE) {
    const res = await apiFetch(`/animations/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ appClientIds }),
    });
    if (!res.ok) throw new Error(`アプリ配信設定の変更に失敗しました (${res.status})`);
    return (await res.json()) as Animation;
  }
  const items = loadLocal().map((a) => (a.id === id ? { ...a, appClientIds } : a));
  saveLocal(items);
  const updated = items.find((a) => a.id === id);
  if (!updated) throw new Error('ローカル投稿のみアプリ配信設定を変更できます');
  return updated;
}

// ---- お気に入り ---------------------------------------------------------------

export interface FavoriteResult {
  id: string;
  likes: number;
  favorited: boolean;
}

/** お気に入りに追加（要ログイン・冪等）。 */
export async function favoriteAnimation(id: string): Promise<FavoriteResult> {
  if (API_BASE) {
    const res = await apiFetch(`/animations/${id}/favorite`, { method: 'POST' });
    if (!res.ok) throw new Error(`お気に入りに失敗しました (${res.status})`);
    return (await res.json()) as FavoriteResult;
  }
  const ids = new Set(loadFavIds());
  ids.add(id);
  saveFavIds([...ids]);
  const item = [...loadLocal(), ...mockAnimations].find((a) => a.id === id);
  // モックでは元データの likes を書き換えないため、お気に入り中は +1 した値を返して実挙動を再現する。
  return { id, likes: (item?.likes ?? 0) + 1, favorited: true };
}

/** お気に入りを解除（要ログイン・冪等）。 */
export async function unfavoriteAnimation(id: string): Promise<FavoriteResult> {
  if (API_BASE) {
    const res = await apiFetch(`/animations/${id}/favorite`, { method: 'DELETE' });
    if (!res.ok) throw new Error(`お気に入り解除に失敗しました (${res.status})`);
    return (await res.json()) as FavoriteResult;
  }
  saveFavIds(loadFavIds().filter((x) => x !== id));
  const item = [...loadLocal(), ...mockAnimations].find((a) => a.id === id);
  return { id, likes: item?.likes ?? 0, favorited: false };
}

// ---- マイページ ---------------------------------------------------------------

/** 自分の投稿一覧（public/private とも）。 */
export async function fetchMyAnimations(opts: ListPageOptions = {}): Promise<PagedAnimations> {
  if (API_BASE) {
    const params = new URLSearchParams();
    if (opts.limit) params.set('limit', String(opts.limit));
    if (opts.cursor) params.set('cursor', opts.cursor);
    addListFilters(params, opts);
    const qs = params.toString();
    const res = await apiFetch(`/me/animations${qs ? `?${qs}` : ''}`);
    if (!res.ok) throw new Error(`投稿一覧の取得に失敗しました (${res.status})`);
    const items = (await res.json()) as Animation[];
    return { items, nextCursor: res.headers.get('X-Next-Cursor') ?? undefined };
  }
  const filtered = loadLocal().filter((animation) =>
    (opts.tags ?? []).every((tag) => animation.tags.includes(tag)) &&
    (!opts.category || (opts.category === '__uncategorized__' ? !animation.category : animation.category === opts.category)) &&
    (!opts.q || animation.title.toLowerCase().includes(opts.q.toLowerCase()) || animation.author.toLowerCase().includes(opts.q.toLowerCase())),
  );
  return paginateMock(filtered, opts, DEFAULT_LIST_LIMIT);
}

/** お気に入り一覧。 */
export async function fetchMyFavorites(): Promise<Animation[]> {
  if (API_BASE) {
    const res = await apiFetch('/me/favorites');
    if (!res.ok) throw new Error(`お気に入り一覧の取得に失敗しました (${res.status})`);
    return (await res.json()) as Animation[];
  }
  const favIds = new Set(loadFavIds());
  return [...loadLocal(), ...mockAnimations].filter((a) => favIds.has(a.id));
}

/** お気に入り済み ID の集合（トグル表示用）。未ログインなら空。 */
export async function fetchFavoriteIds(): Promise<Set<string>> {
  try {
    return new Set((await fetchMyFavorites()).map((a) => a.id));
  } catch {
    return new Set();
  }
}

// ---- 連携中のアプリ（サードパーティ OAuth）------------------------------------

export interface ConnectedApp {
  clientId: string;
  appName: string;
  appDescription: string;
  scope: string[];
  createdAt: string;
  updatedAt: string;
}

const CONNECTED_APPS_KEY = 'vrma-hub:local-connected-apps';

function loadConnectedApps(): ConnectedApp[] {
  try {
    const raw = localStorage.getItem(CONNECTED_APPS_KEY);
    return raw ? (JSON.parse(raw) as ConnectedApp[]) : [];
  } catch {
    return [];
  }
}

/**
 * 連携中のサードパーティアプリ一覧（新着順）。
 * モックモードでは実際に OAuth 連携する導線がこのフロントには無いため、常に空配列を返す
 * （localStorage には何も書き込まれず、常に空のまま）。
 */
export async function fetchConnectedApps(): Promise<ConnectedApp[]> {
  if (API_BASE) {
    const res = await apiFetch('/me/connected-apps');
    if (!res.ok) throw new Error(`連携アプリ一覧の取得に失敗しました (${res.status})`);
    return (await res.json()) as ConnectedApp[];
  }
  return loadConnectedApps();
}

/** 連携解除（要ログイン・冪等）。 */
export async function disconnectApp(clientId: string): Promise<void> {
  if (API_BASE) {
    const res = await apiFetch(`/me/connected-apps/${encodeURIComponent(clientId)}`, { method: 'DELETE' });
    if (!res.ok && res.status !== 204) throw new Error(`連携解除に失敗しました (${res.status})`);
    return;
  }
  const items = loadConnectedApps().filter((a) => a.clientId !== clientId);
  localStorage.setItem(CONNECTED_APPS_KEY, JSON.stringify(items));
}

// ---- プロフィール（ニックネーム・アイコン）--------------------------------------

export interface UserProfile {
  sub: string;
  nickname: string;
  iconUrl?: string;
  createdAt: string;
  updatedAt: string;
}

const MY_PROFILE_KEY = 'vrma-hub:my-profile';

/**
 * プロフィール保存成功時に window へ発火するイベント名。
 * ヘッダーのアバター表示など、別コンポーネントが最新プロフィールを再取得するために使う。
 */
export const PROFILE_UPDATED_EVENT = 'vrma-hub:profile-updated';

function loadMyProfile(): UserProfile | null {
  try {
    const raw = localStorage.getItem(MY_PROFILE_KEY);
    return raw ? (JSON.parse(raw) as UserProfile) : null;
  } catch {
    return null;
  }
}

function saveMyProfile(profile: UserProfile): void {
  localStorage.setItem(MY_PROFILE_KEY, JSON.stringify(profile));
}

/** nickname のバリデーション（バックエンドと同じ規則: trim 後 1〜32 文字、制御文字禁止）。 */
function validateNicknameMock(nickname: string): string {
  const trimmed = nickname.trim();
  // eslint-disable-next-line no-control-regex
  if (trimmed.length < 1 || trimmed.length > 32 || /[\x00-\x1f\x7f]/.test(trimmed)) {
    throw new Error('nickname は1〜32文字で指定してください');
  }
  return trimmed;
}

/** 自分のプロフィール（要ログイン）。未作成なら null（バックエンドは 404 にせず null で返す）。 */
export async function fetchMyProfile(): Promise<UserProfile | null> {
  if (API_BASE) {
    const res = await apiFetch('/me/profile');
    if (!res.ok) throw new Error(`プロフィールの取得に失敗しました (${res.status})`);
    return (await res.json()) as UserProfile | null;
  }
  return loadMyProfile();
}

/** プロフィールの作成/更新（要ログイン）。初回作成時は nickname が必須。 */
export async function updateMyProfile(patch: {
  nickname?: string;
  iconKey?: string | null;
}): Promise<UserProfile> {
  if (API_BASE) {
    const res = await apiFetch('/me/profile', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    });
    if (res.status === 400) {
      // badRequest() は { error: message } の形で返す（backend/src/lib/http.ts 参照）。
      const body = (await res.json().catch(() => null)) as { error?: string } | null;
      throw new Error(body?.error ?? `プロフィールの更新に失敗しました (${res.status})`);
    }
    if (!res.ok) throw new Error(`プロフィールの更新に失敗しました (${res.status})`);
    const updated = (await res.json()) as UserProfile;
    window.dispatchEvent(new Event(PROFILE_UPDATED_EVENT));
    return updated;
  }
  const existing = loadMyProfile();
  if (!existing && patch.nickname === undefined) {
    throw new Error('初回登録時は nickname が必須です');
  }
  const nickname =
    patch.nickname !== undefined ? validateNicknameMock(patch.nickname) : (existing as UserProfile).nickname;
  const now = new Date().toISOString();
  const updated: UserProfile = {
    sub: existing?.sub ?? 'local',
    nickname,
    iconUrl:
      patch.iconKey === null ? undefined : patch.iconKey !== undefined ? patch.iconKey : existing?.iconUrl,
    createdAt: existing?.createdAt ?? now,
    updatedAt: now,
  };
  saveMyProfile(updated);
  window.dispatchEvent(new Event(PROFILE_UPDATED_EVENT));
  return updated;
}

/** アイコン用 presigned PUT URL を発行する（要ログイン）。 */
export async function createAvatarUploadUrl(
  contentType: 'image/png' | 'image/jpeg' | 'image/webp',
): Promise<PresignResponse> {
  if (API_BASE) {
    const res = await apiFetch('/me/profile/avatar-upload-url', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ contentType }),
    });
    if (!res.ok) throw new Error(`アップロードURLの取得に失敗しました (${res.status})`);
    return (await res.json()) as PresignResponse;
  }
  throw new Error('モックモードでは使用できません');
}

const ALLOWED_AVATAR_TYPES = ['image/png', 'image/jpeg', 'image/webp'];

/** File を受け取って presigned URL 経由でアップロードし、iconKey を返す。 */
export async function uploadAvatar(file: File): Promise<string> {
  if (!ALLOWED_AVATAR_TYPES.includes(file.type)) {
    throw new Error('画像形式は png / jpeg / webp のみ対応しています');
  }
  if (API_BASE) {
    const { uploadUrl, key } = await createAvatarUploadUrl(
      file.type as 'image/png' | 'image/jpeg' | 'image/webp',
    );
    const putRes = await fetch(uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': file.type },
      body: file,
    });
    if (!putRes.ok) throw new Error(`アイコンのアップロードに失敗しました (${putRes.status})`);
    return key;
  }
  // モックでは iconKey と iconUrl の区別が実質不要なため、dataURL をそのまま返す。
  return blobToDataUrl(file);
}

// ---- フォロー -----------------------------------------------------------------

const FOLLOWING_KEY = 'vrma-hub:local-following';
/** ローカル固定ユーザー（auth/AuthContext.tsx の LOCAL_USER）と揃える。 */
const LOCAL_SUB = 'local';
/** モックの合成 sub のプレフィックス。mockAnimations の author 名から生成する。 */
const MOCK_SUB_PREFIX = 'mock:';

/** followeeSub -> フォローした日時（ISO）。 */
function loadFollowing(): Record<string, string> {
  try {
    const raw = localStorage.getItem(FOLLOWING_KEY);
    return raw ? (JSON.parse(raw) as Record<string, string>) : {};
  } catch {
    return {};
  }
}

function saveFollowing(map: Record<string, string>): void {
  localStorage.setItem(FOLLOWING_KEY, JSON.stringify(map));
}

/**
 * モックモード専用: sub からユーザープロフィールを合成する。
 * 'local' は自分自身（プロフィール未作成なら仮の表示名で代替）、
 * 'mock:<author>' は mockAnimations の author 名を持つ合成ユーザー、
 * それ以外は存在しないユーザーとして null を返す。
 */
function buildMockProfile(sub: string): UserProfile | null {
  if (sub === LOCAL_SUB) {
    const existing = loadMyProfile();
    if (existing) return existing;
    const now = new Date(0).toISOString();
    return { sub: LOCAL_SUB, nickname: 'you', createdAt: now, updatedAt: now };
  }
  if (!sub.startsWith(MOCK_SUB_PREFIX)) return null;
  const author = sub.slice(MOCK_SUB_PREFIX.length);
  const posts = mockAnimations.filter((a) => a.author === author);
  if (posts.length === 0) return null;
  const sorted = [...posts].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  );
  return {
    sub,
    nickname: author,
    createdAt: sorted[0].createdAt,
    updatedAt: sorted[sorted.length - 1].createdAt,
  };
}

/** 指定ユーザーの公開プロフィール。未登録なら null。 */
export async function fetchUserProfile(sub: string): Promise<UserProfile | null> {
  if (API_BASE) {
    const res = await apiFetch(`/users/${encodeURIComponent(sub)}`);
    if (res.status === 404) return null;
    if (!res.ok) throw new Error(`プロフィールの取得に失敗しました (${res.status})`);
    return (await res.json()) as UserProfile;
  }
  return buildMockProfile(sub);
}

/** 指定ユーザーの公開VRMA一覧（新着順）。 */
export async function fetchUserAnimations(sub: string, opts: ListPageOptions = {}): Promise<PagedAnimations> {
  if (API_BASE) {
    const params = new URLSearchParams();
    if (opts.limit) params.set('limit', String(opts.limit));
    if (opts.cursor) params.set('cursor', opts.cursor);
    addListFilters(params, opts);
    const qs = params.toString();
    const res = await apiFetch(`/users/${encodeURIComponent(sub)}/animations${qs ? `?${qs}` : ''}`);
    if (!res.ok) throw new Error(`投稿一覧の取得に失敗しました (${res.status})`);
    const items = (await res.json()) as Animation[];
    return { items, nextCursor: res.headers.get('X-Next-Cursor') ?? undefined };
  }
  const items =
    sub === LOCAL_SUB
      ? loadLocal().filter((a) => (a.visibility ?? 'public') === 'public')
      : sub.startsWith(MOCK_SUB_PREFIX)
        ? mockAnimations.filter((a) => a.author === sub.slice(MOCK_SUB_PREFIX.length))
        : [];
  const sorted = items.filter((animation) =>
    (opts.tags ?? []).every((tag) => animation.tags.includes(tag)) &&
    (!opts.category || (opts.category === '__uncategorized__' ? !animation.category : animation.category === opts.category)) &&
    (!opts.q || animation.title.toLowerCase().includes(opts.q.toLowerCase()) || animation.author.toLowerCase().includes(opts.q.toLowerCase())),
  ).sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  return paginateMock(sorted, opts, DEFAULT_LIST_LIMIT);
}

/** 自分が対象ユーザーをフォロー中か（要ログイン）。未ログイン時は false。 */
export async function fetchFollowStatus(sub: string): Promise<boolean> {
  if (API_BASE) {
    const res = await apiFetch(`/users/${encodeURIComponent(sub)}/follow-status`);
    if (res.status === 401) return false;
    if (!res.ok) throw new Error(`フォロー状態の取得に失敗しました (${res.status})`);
    const body = (await res.json()) as { following: boolean };
    return body.following;
  }
  return sub in loadFollowing();
}

export interface FollowResult {
  followerSub: string;
  followeeSub: string;
  following: boolean;
}

/** フォロー（要ログイン・冪等）。 */
export async function followUser(sub: string): Promise<FollowResult> {
  if (API_BASE) {
    const res = await apiFetch(`/users/${encodeURIComponent(sub)}/follow`, { method: 'POST' });
    if (!res.ok) throw new Error(`フォローに失敗しました (${res.status})`);
    return (await res.json()) as FollowResult;
  }
  const map = loadFollowing();
  if (!(sub in map)) map[sub] = new Date().toISOString();
  saveFollowing(map);
  return { followerSub: LOCAL_SUB, followeeSub: sub, following: true };
}

/** フォロー解除（要ログイン・冪等）。 */
export async function unfollowUser(sub: string): Promise<FollowResult> {
  if (API_BASE) {
    const res = await apiFetch(`/users/${encodeURIComponent(sub)}/follow`, { method: 'DELETE' });
    if (!res.ok) throw new Error(`フォロー解除に失敗しました (${res.status})`);
    return (await res.json()) as FollowResult;
  }
  const map = loadFollowing();
  delete map[sub];
  saveFollowing(map);
  return { followerSub: LOCAL_SUB, followeeSub: sub, following: false };
}

/** 自分がフォロー中のユーザーのプロフィール一覧（フォローした日時の降順）。 */
export async function fetchMyFollowing(): Promise<UserProfile[]> {
  if (API_BASE) {
    const res = await apiFetch('/me/following');
    if (!res.ok) throw new Error(`フォロー中一覧の取得に失敗しました (${res.status})`);
    return (await res.json()) as UserProfile[];
  }
  const entries = Object.entries(loadFollowing()).sort(
    (a, b) => new Date(b[1]).getTime() - new Date(a[1]).getTime(),
  );
  return entries.map(([sub]) => buildMockProfile(sub)).filter((p): p is UserProfile => p !== null);
}

/** フォロー中ユーザーの公開VRMAを新着順に返す。 */
export async function fetchFollowingAnimations(opts: ListPageOptions = {}): Promise<PagedAnimations> {
  if (API_BASE) {
    const params = new URLSearchParams();
    if (opts.limit) params.set('limit', String(opts.limit));
    if (opts.cursor) params.set('cursor', opts.cursor);
    addListFilters(params, opts);
    const qs = params.toString();
    const res = await apiFetch(`/me/following-animations${qs ? `?${qs}` : ''}`);
    if (!res.ok) throw new Error(`フォロー中の新着取得に失敗しました (${res.status})`);
    const items = (await res.json()) as Animation[];
    return { items, nextCursor: res.headers.get('X-Next-Cursor') ?? undefined };
  }
  const subs = Object.keys(loadFollowing());
  const lists = await Promise.all(subs.map((sub) => fetchUserAnimations(sub, opts)));
  const merged = lists
    .flatMap((r) => r.items)
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  return paginateMock(merged, opts, DEFAULT_LIST_LIMIT);
}

// ---- お問い合わせ -------------------------------------------------------------

export interface SendContactInput {
  category: 'general' | 'rights' | 'privacy' | 'bug' | 'other';
  name?: string;
  email: string;
  message: string;
  /** bot 対策の honeypot。人間のユーザーは入力しない想定（値が入ると送信されない）。 */
  websiteUrl?: string;
}

/** お問い合わせフォームの送信（認証不要・匿名で送信可）。 */
export async function sendContact(input: SendContactInput): Promise<void> {
  if (API_BASE) {
    const res = await fetch(`${API_BASE}/contact`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
    if (res.status === 429) throw new Error('しばらく時間をおいてから再送信してください。');
    if (!res.ok) throw new Error('送信に失敗しました。時間をおいて再度お試しください。');
    return;
  }
  console.log('mock sendContact', input);
}

// ---- 通報 ---------------------------------------------------------------------

export interface ReportInput {
  category: 'rights' | 'inappropriate' | 'spam' | 'other';
  /** 任意の補足説明（最長500文字）。 */
  detail?: string;
}

/** アニメーションの通報（要ログイン）。同一ユーザー×同一アニメーションの通報は1件に集約される。 */
export async function reportAnimation(id: string, input: ReportInput): Promise<void> {
  if (API_BASE) {
    const res = await apiFetch(`/animations/${id}/report`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
    if (res.status === 401) throw new Error('ログインが必要です');
    if (res.status === 404) throw new Error('対象のアニメーションが見つかりません');
    if (!res.ok) throw new Error(`通報の送信に失敗しました (${res.status})`);
    return;
  }
  console.log('mock reportAnimation', id, input);
}

// ---- 再生数・ダウンロード数のカウント -------------------------------------------

/**
 * 再生数/ダウンロード数のカウント（認証不要・fire-and-forget）。
 * UI をブロックしないよう失敗しても throw せず console.error に留める。モックモードでは何もしない。
 */
export function countAnimation(id: string, kind: 'play' | 'download'): void {
  if (!API_BASE) return;
  apiFetch(`/animations/${id}/count`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ kind }),
  })
    .then((res) => {
      if (!res.ok) console.error(`countAnimation に失敗しました (${res.status})`);
    })
    .catch((e) => console.error('countAnimation に失敗しました', e));
}
